import { FeatureFlag } from '@fluenceur/types';
import { useQueryStates } from 'nuqs';
import { useMemo, useState } from 'react';
import SendInvitationModal from '@/components/actions/send-invitation/send-invitation-modal';
import { useAuth } from '@/contexts/auth-context';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useUgcAvailability } from '@/hooks/use-ugc-availability';
import { useContactProfileQuery } from '@/hooks/queries/use-contact-profile-query';
import type { CollabWizardType } from '@/components/actions/send-invitation/collab-types';
import { platformEnumOf } from '../platform-of';
import { useContactDetailsQuery } from '@/hooks/queries/use-contact-details-query';
import { useProposeCreatorResolution } from '@/components/actions/send-invitation/use-propose-creator-resolution';
import { useContactSummaryQuery } from '@/hooks/queries/use-contact-summary-query';
import { useResolveContactQuery } from '@/hooks/queries/use-resolve-contact-query';
import { contactDrawerParsers } from '../contact-drawer-url-state';
import { useContactResolveRef } from '../use-contact-resolve-ref';
import { useResolvedContactIdentityId } from '../use-resolved-contact-identity-id';

/**
 * Wires the contact card's "Propose a collaboration" CTA to the shared
 * invitation modal, opened on the collaboration-type step.
 *
 * The recipient of a collab is a person (a resolved identity), affiliated or
 * not. So the CTA is available as soon as the org holds the COLLABS feature, the
 * contact has a resolved identity AND at least one reachable channel: a social
 * profile OR a brand-known email.
 * Without an identity, or with neither channel, the recipient is unreachable and
 * the CTA stays hidden (`onPropose` is `undefined`).
 *
 * When a social profile exists, the click first RESOLVES the Creator and only
 * then opens the wizard (the CTA reports `isProposing` meanwhile). Opening
 * immediately used to race the provider's one-shot seed on a cold creator cache:
 * the seed ran without `creator`, so an email-less contact had no invitation
 * target and the Affiliation type dead-ended at the program step. Holding the
 * open until the fetch settles guarantees the seed carries the creator whenever
 * one exists; a failed/absent enrichment still opens on the identity + email
 * alone (the shareable link remains the floor). Returns the modal element to
 * mount once, high in the drawer tree, so a mobile screen swap can't unmount it
 * mid-flow.
 */
export const useContactProposeCollab = () => {
  const [{ contactPlatform }] = useQueryStates(contactDrawerParsers);
  const collabsEnabled = useFeatureFlag(FeatureFlag.COLLABS);
  const { user } = useAuth();
  const identityId = useResolvedContactIdentityId();
  const { data: summary } = useContactSummaryQuery(user?.currentBrandId, identityId);
  // The click intent, with the path it asked for: the generic CTA opens on the
  // type step, the UGC row straight on UGC. The wizard itself only opens once
  // the creator enrichment settled, so the provider's seed never runs ahead of
  // the fetch.
  const [requested, setRequested] = useState<{ type: CollabWizardType | null } | null>(null);
  const isRequested = requested !== null;
  const ugcAvailable = useUgcAvailability();
  // Her UGC offer, from the same profile slice the sheet shows (cache shared):
  // it preselects UGC and puts her starting price beside the amount.
  const { data: profile } = useContactProfileQuery(identityId, platformEnumOf(contactPlatform));

  // Brand-known email, preferring the brand's contact override over the
  // resolved identity's primary email. Both slices share their cache with the
  // drawer, so this adds no extra request. `null` when the brand has no email —
  // the modal then falls back to the DM/comment channel.
  const { data: details } = useContactDetailsQuery(identityId);
  const resolveRef = useContactResolveRef();
  const { data: resolved } = useResolveContactQuery(resolveRef);
  const recipientEmail = details?.email ?? resolved?.primaryEmail ?? null;

  // Eligibility only (the CTA needs it BEFORE any click): does a social profile
  // exist at all? The click-time resolution below re-derives its own profile
  // from the same cached summary — and, unlike this optimistic read, WAITS for
  // it on a cold cache.
  const socialProfileId = useMemo(() => {
    const profiles = summary?.socialProfiles ?? [];
    const active = profiles.find((profile) => profile.platform === contactPlatform);
    return (active ?? profiles[0])?.socialProfileId ?? null;
  }, [summary, contactPlatform]);

  // Shared resolve-then-open enrichment (same hook as the collabs picker). No
  // known profile id is passed: the hook owns the summary hop, preferring the
  // drawer's active platform, and holds `isResolving` until BOTH the summary and
  // the creator fetch settle — a cold summary cache no longer opens seedless.
  const { creator, isResolving: isProposing } = useProposeCreatorResolution({
    requested: isRequested,
    identityId,
    preferredPlatform: contactPlatform,
  });
  const isOpen = isRequested && !isProposing;

  // Available only when the org has the COLLABS feature (so the sponsored wizard
  // won't dead-end at the backend), with a resolved identity and at least one
  // reachable channel (social profile OR brand-known email). Hidden otherwise —
  // and hidden too while the flag query loads, since `useFeatureFlag` reads
  // `false` until it settles.
  const canPropose = collabsEnabled && !!identityId && (!!socialProfileId || !!recipientEmail);
  const onPropose = canPropose ? () => setRequested({ type: null }) : undefined;
  const onProposeUgc =
    canPropose && ugcAvailable ? () => setRequested({ type: 'ugc' }) : undefined;

  const modal = (
    <SendInvitationModal
      isOpen={isOpen}
      onClose={() => setRequested(null)}
      creator={creator ?? undefined}
      recipientEmail={recipientEmail}
      recipientName={resolved?.displayName ?? null}
      recipientAvatarUrl={resolved?.avatarUrl ?? null}
      initialView="typeSelection"
      initialCollabType={requested?.type ?? undefined}
      recipientUgcOffer={profile?.ugcOffer ?? null}
      creatorIdentityId={identityId}
    />
  );

  return { onPropose, onProposeUgc, isProposing, modal };
};
